import constate from "constate";
import { useCallback, useMemo } from "react";

interface ReviewPromptSignalContextProps {
  hasReviewPromptSignal: boolean;
  setHasReviewPromptSignal: (next: boolean) => void;
}

function useReviewPromptSignal(
  props: ReviewPromptSignalContextProps
): ReviewPromptSignalContextProps {
  const { hasReviewPromptSignal, setHasReviewPromptSignal } = props;
  return useMemo(
    () => ({ hasReviewPromptSignal, setHasReviewPromptSignal }),
    [hasReviewPromptSignal, setHasReviewPromptSignal]
  );
}

export const [ReviewPromptSignalProvider, useReviewPromptSignalContext] =
  constate(useReviewPromptSignal);

/**
 * Flag the current session as having produced a review-prompt signal the
 * Knock dashboard's rating-prompt rule can target on. Monotonic by design —
 * once flipped, stays true until the provider unmounts (page reload, login).
 * The false → true transition is what triggers Knock to re-evaluate
 * `targetParams`, so this hook intentionally doesn't expose the false-setter
 * (D-08).
 */
export function useMarkReviewPromptSignal(): () => void {
  const { setHasReviewPromptSignal } = useReviewPromptSignalContext();
  return useCallback(() => {
    setHasReviewPromptSignal(true);
  }, [setHasReviewPromptSignal]);
}

/**
 * Read access to the review-prompt-signal flag. Primarily intended for tests;
 * production code paths only set the flag via `useMarkReviewPromptSignal`.
 */
export function useHasReviewPromptSignal(): boolean {
  return useReviewPromptSignalContext().hasReviewPromptSignal;
}
